import { useEffect, useState } from "react";
import { CreateAdmin } from "../components/admin/CreateAdmin";
import { UpdateAdmin } from "../components/admin/UpdateAdmin";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  deleteAdmin,
  list,
  toggleStatus,
  forceLogoutAdmin,
} from "../redux/slices/adminSlice";
import { useQueryParams } from "../hooks/useQueryParams";
import { Pagination } from "../components/common/pagination/Pagination";
import { TiPencil } from "react-icons/ti";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdToggleOn, MdToggleOff } from "react-icons/md";
import { TbLogout2 } from "react-icons/tb";
import { FaUserShield } from "react-icons/fa";
import { toast } from "react-toastify";
import { getErrorMessage } from "../utils/errorHandler";
import { ConfirmModal } from "../components/common/ConfirmModal";
import { PageHeader } from "../components/common/PageHeader";
import { SearchInput } from "../components/common/SearchInput";
import { EmptyState } from "../components/common/EmptyState";
import { StatusBadge } from "../components/common/StatusBadge";
import { DataTable } from "../components/common/DataTable";

export const Admin = () => {
  const dispatch = useAppDispatch();
  const {
    admins,
    pagination,
    admin: currentAdmin,
    loading,
  } = useAppSelector((state) => state.admin);
  const isSuperAdmin = currentAdmin?.role === "super-admin";

  const [isCreate, setIsCreate] = useState(false);
  const [editAdminId, setEditAdminId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmForceLogout, setConfirmForceLogout] = useState<string | null>(
    null,
  );

  const { getParam, updateParams } = useQueryParams();
  const search = getParam("search", "");
  const page = Number(getParam("page", 1));
  const isActive = getParam("isActive", "all");
  const limit = Number(getParam("limit", 10));

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateParams({ [name]: value, page: 1 });
  };

  const parsedIsActive =
    isActive === "all" ? undefined : isActive === "true" ? true : false;

  useEffect(() => {
    dispatch(list({ search, page, limit, isActive: parsedIsActive }));
  }, [dispatch, search, page, limit, parsedIsActive]);

  const handleDeleteAdmin = async () => {
    if (!confirmDelete) return;
    try {
      await dispatch(deleteAdmin(confirmDelete)).unwrap();
      toast.success("Xóa admin thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await dispatch(toggleStatus({ id, isActive: !currentStatus })).unwrap();
      toast.success(
        currentStatus ? "Đã vô hiệu hóa admin" : "Đã kích hoạt admin",
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleForceLogout = async () => {
    if (!confirmForceLogout) return;
    try {
      await dispatch(forceLogoutAdmin(confirmForceLogout)).unwrap();
      toast.success("Đã đăng xuất admin thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmForceLogout(null);
    }
  };

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý admin"
        subtitle={pagination ? `Tổng ${pagination.total} tài khoản` : undefined}
        actions={
          isSuperAdmin ? (
            <button
              type="button"
              onClick={() => setIsCreate(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer text-sm text-white rounded-lg font-medium"
            >
              + Thêm admin
            </button>
          ) : undefined
        }
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={(v) => updateParams({ search: v, page: 1 })}
            placeholder="Tìm admin theo tên..."
            className="w-full lg:w-80"
          />
          <div className="flex items-center gap-2">
            <select
              name="isActive"
              value={isActive}
              onChange={handleChange}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="true">Hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>
            <select
              name="limit"
              value={limit}
              onChange={handleChange}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="10">10 / trang</option>
              <option value="20">20 / trang</option>
              <option value="30">30 / trang</option>
              <option value="50">50 / trang</option>
            </select>
          </div>
        </div>

        <DataTable
          data={admins}
          rowKey={(a) => a._id}
          loading={loading.list}
          columns={[
            {
              key: "name",
              header: "Tên",
              align: "left",
              render: (a) => (
                <span className="font-medium text-gray-900">{a.name}</span>
              ),
            },
            {
              key: "email",
              header: "Email",
              align: "left",
              render: (a) => <span className="text-gray-600">{a.email}</span>,
            },
            {
              key: "role",
              header: "Vai trò",
              align: "center",
              render: (a) =>
                a.role === "super-admin" ? (
                  <StatusBadge tone="info" label="Super Admin" />
                ) : (
                  <StatusBadge tone="neutral" label="Admin" />
                ),
            },
            {
              key: "status",
              header: "Trạng thái",
              align: "center",
              render: (a) =>
                a.isActive ? (
                  <StatusBadge tone="success" label="Hoạt động" />
                ) : (
                  <StatusBadge tone="danger" label="Ngừng" />
                ),
            },
            ...(isSuperAdmin
              ? [
                  {
                    key: "actions",
                    header: "Thao tác",
                    align: "right" as const,
                    width: "180px",
                    render: (a: (typeof admins)[number]) => (
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          title="Sửa admin"
                          onClick={() => setEditAdminId(a._id)}
                          className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                        >
                          <TiPencil className="text-lg" />
                        </button>
                        <button
                          type="button"
                          title={a.isActive ? "Vô hiệu hóa" : "Kích hoạt"}
                          onClick={() =>
                            handleToggleStatus(a._id, a.isActive)
                          }
                          className={`p-2 rounded-lg transition-colors cursor-pointer ${
                            a.isActive
                              ? "text-emerald-600 hover:bg-emerald-50"
                              : "text-gray-400 hover:bg-gray-50"
                          }`}
                        >
                          {a.isActive ? (
                            <MdToggleOn className="text-xl" />
                          ) : (
                            <MdToggleOff className="text-xl" />
                          )}
                        </button>
                        <button
                          type="button"
                          title="Đăng xuất khỏi mọi thiết bị"
                          onClick={() => setConfirmForceLogout(a._id)}
                          className="p-2 rounded-lg text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer"
                        >
                          <TbLogout2 className="text-lg" />
                        </button>
                        <button
                          type="button"
                          title="Xóa admin"
                          onClick={() => setConfirmDelete(a._id)}
                          className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <RiDeleteBinLine className="text-lg" />
                        </button>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
          empty={
            <EmptyState
              icon={<FaUserShield />}
              title={search ? "Không tìm thấy admin nào" : "Chưa có admin nào"}
              description={
                search ? "Thử tìm với từ khóa khác" : undefined
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {isCreate && <CreateAdmin handleShowFormCreate={setIsCreate} />}
      {editAdminId && (
        <UpdateAdmin
          adminId={editAdminId}
          handleClose={() => setEditAdminId(null)}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa admin này? Hành động này không thể hoàn tác."
          onConfirm={handleDeleteAdmin}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {confirmForceLogout && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn đăng xuất admin này khỏi tất cả thiết bị?"
          onConfirm={handleForceLogout}
          onCancel={() => setConfirmForceLogout(null)}
        />
      )}
    </div>
  );
};
